import { ErrorPreview } from "@/components/pages/error-preview";
import { Preview } from "@/components/pages/preview";
import { ShareablePreview } from "@/components/pages/shareable-preview";
import type { Activity, ActivityFlowConsents } from "@lib/activity";
import type { Config } from "@lib/config/utils";
import { type LandingPageChoice } from "@lib/landing";
import { createRoute } from "@tanstack/react-router";
import { Route as rootRoute, getContext } from "./root";
import { z } from "zod";
import { fallback, zodValidator } from "@tanstack/zod-adapter";
import type { ConfigEnv } from "@lib/config/eval";
import type { Ticket } from "@lib/ticket";
import { logger } from "@lib/logger.client";

export type Props = {
  activity: Activity;
  config: Config;
  encodedActivityId: string;
  flowConsents: ActivityFlowConsents;
  ticket?: Ticket;
};

// Note: error splats ("out_of_stock" | "closed" | "claimed") are intercepted
// earlier by the `_splat?.startsWith("error")` branch and dispatched to
// `ErrorPreview`, so they are intentionally not part of this schema.
const Schema = z
  .enum(["", "landing", "complete"])
  .or(z.coerce.number())
  .transform((v) =>
    v === "" ? "landing" : v === "complete" ? "completion" : v,
  );

// IsPreviewUrl returns true if the URL is a preview route
export function IsPreviewUrl(): boolean {
  if (typeof window === "undefined") {
    logger.error("IsPreviewUrl called in a non-browser context");
    return false;
  }
  return (
    window.location.pathname.includes("/flow/config-preview/") ||
    window.location.pathname.includes("/flow/shareable-preview/") ||
    new URLSearchParams(window.location.search).get("mode") === "preview"
  );
}

// allow either boolean or string for feature flags
const previewFlagSchema = z
  .union([z.boolean(), z.enum(["true", "false"])])
  .optional();

// These flags are passed through as raw strings on purpose. If we used
// a `z.transform(v => v === "true")` here, tanstack's search stringifier
// would serialise the transformed boolean back onto the URL, appending
// `?showSmartPrompts=false&showProgressBar=false` to preview links that
// never set them. Keeping the schema pass-through leaves `undefined`
// values out of the URL entirely. Strict matching — only the literal
// string "true" enables the flag — is enforced by the consumers
// comparing `=== "true"`; do NOT swap that for truthiness or for
// `z.coerce.boolean()`, which treats every non-empty string (including
// "false" and "0") as truthy.
export const configPreviewSearchSchema = z.object({
  env: fallback(z.string(), ""),
  showSmartPrompts: previewFlagSchema,
  showProgressBar: previewFlagSchema,
});

// Default to false. We want to avoid showing features in the preview unless they are explicitly turned on
export function isPreviewFlagEnabled(
  value: boolean | string | undefined,
): boolean {
  return value === true || value === "true";
}

export const ConfigRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/flow/config-preview/$flowType/$encodedActivityId/$id/$",
  validateSearch: zodValidator(configPreviewSearchSchema),
  loader: ({ params }) => ({
    ...params,
    ...getContext<Props>(),
  }),
  component: function Page() {
    const props = ConfigRoute.useLoaderData();
    const { env, showProgressBar } = ConfigRoute.useSearch();

    // The progress bar is only valid for the `review` flow. Force it off
    // for any other flowType (claim, community, double_optin, delayed,
    // review_delayed, etc.) regardless of what the query string says.
    const effectiveShowProgressBar =
      isPreviewFlagEnabled(showProgressBar) && props.flowType === "review";

    const parsedEnv = Object.fromEntries(
      new URLSearchParams(decodeURIComponent(env)).entries(),
    ) as unknown as ConfigEnv;

    if (props._splat?.startsWith("error")) {
      const errorType = props._splat.split("/")[1] ?? "out_of_stock";
      return (
        <ErrorPreview
          type={props.flowType}
          activity={props.activity}
          page={errorType as LandingPageChoice}
          config={props.config}
          env={parsedEnv}
        />
      );
    }
    const page = Schema.safeParse(props._splat);
    if (page.success) {
      return (
        <Preview
          type={props.flowType}
          activity={props.activity}
          page={page.data}
          flowConsents={props.flowConsents}
          config={props.config}
          env={parsedEnv}
          ticket={props.ticket}
          showProgressBar={effectiveShowProgressBar}
        />
      );
    }
  },
});

export const ShareableRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/flow/shareable-preview/$flowType/$encodedActivityId/$id/$",
  loader: ({ params }) => ({ ...params, ...getContext<Props>() }),
  component: function Page() {
    const { activity, config, encodedActivityId, flowType, flowConsents } =
      ShareableRoute.useLoaderData();

    return (
      <ShareablePreview
        activity={activity}
        config={config}
        encodedActivityId={encodedActivityId}
        type={flowType}
        flowConsents={flowConsents}
      />
    );
  },
});
